import { useState, useRef, useEffect } from 'react'
import { Send, Paperclip } from 'lucide-react'
import { EmojiPicker } from './EmojiPicker'
import { useTranslation } from 'react-i18next'

interface MessageInputProps {
  onSendMessage: (content: string, attachments?: any[]) => Promise<void>
  onTyping: (isTyping: boolean) => void
  disabled?: boolean
  placeholder?: string
  isEditing?: boolean
  editValue?: string
  onCancelEdit?: () => void
  replyTo?: { id: string; content: string; senderName: string } | null
  onCancelReply?: () => void
}

export function MessageInput({
  onSendMessage,
  onTyping,
  disabled = false,
  placeholder = 'Type a message...',
  isEditing = false,
  editValue = '',
  onCancelEdit,
  replyTo,
  onCancelReply,
}: MessageInputProps) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [message])

  // Sync input with external edit value
  useEffect(() => {
    if (isEditing && editValue) {
      setMessage(editValue)
      textareaRef.current?.focus()
    } else if (!isEditing) {
      // setMessage('') // Optional: clear when edit cancelled, or leave draft?
    }
  }, [isEditing, editValue])

  // Handle typing indicator
  const handleInputChange = (value: string) => {
    setMessage(value)

    // Broadcast typing
    if (value.length > 0) {
      onTyping(true)

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      // Stop typing after 3s of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false)
      }, 3000)
    } else {
      onTyping(false)
    }
  }

  const handleSend = async () => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage || isSending) return

    setIsSending(true)
    onTyping(false)

    try {
      await onSendMessage(trimmedMessage)
      setMessage('')

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleEmojiSelect = (emoji: string) => {
    setMessage((prev) => prev + emoji)
    textareaRef.current?.focus()
  }

  return (
    <div className="border-t border-[var(--app-border)] bg-[var(--app-bg-card)] p-4">
      {/* Editing Context Banner */}
      {isEditing && (
        <div className="mb-2 flex items-center justify-between rounded-t-lg border border-[var(--app-border)] bg-[var(--app-bg-input)] px-4 py-2 text-sm">
          <div className="flex items-center gap-2 text-amber-500">
            <span className="font-medium">{t('messages.editingMessage')}</span>
          </div>
          <button
            onClick={() => {
              setMessage('')
              if (onCancelEdit) onCancelEdit()
            }}
            className="text-gray-400 transition-colors hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Replying Context Banner */}
      {replyTo && (
        <div className="mb-2 flex items-center justify-between rounded-t-lg border border-[var(--app-border)] bg-[var(--app-bg-input)] px-4 py-2 text-sm">
          <div className="flex items-center gap-2 text-[var(--app-text-secondary)]">
            <span className="text-[var(--app-text-muted)]">
              {t('messages.replyingTo')} {replyTo.senderName}:
            </span>
            <span className="max-w-[200px] truncate italic opacity-75">{replyTo.content}</span>
          </div>
          <button
            onClick={() => {
              if (onCancelReply) onCancelReply()
            }}
            className="text-gray-400 transition-colors hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      <div className="flex items-end gap-3 bg-[var(--app-bg-card)]">
        {/* Attachment Button */}
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-full p-2 text-[var(--app-text-secondary)] transition-colors hover:bg-[var(--app-bg-elevated)] hover:text-[var(--app-text-primary)]"
            aria-label="Add attachment"
            disabled
            title="Coming soon"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          {/* Emoji Picker - Moved here */}
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            className="rounded-full p-2 text-[var(--app-text-secondary)] transition-colors hover:bg-[var(--app-bg-elevated)] hover:text-amber-500"
          />
        </div>

        {/* Input Container */}
        <div className="relative flex-1 rounded-2xl border border-[var(--app-border)] bg-[var(--app-bg-input)]">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              placeholder === 'Type a message...' ? t('messages.typeMessage') : placeholder
            }
            disabled={disabled || isSending}
            rows={1}
            className="max-h-32 w-full resize-none overflow-y-auto rounded-2xl border-none bg-transparent py-3 pl-4 pr-10 text-[var(--app-text-primary)] placeholder-gray-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          />
        </div>

        {/* Send/Save Button */}
        <button
          onClick={handleSend}
          disabled={!message.trim() || isSending || disabled}
          className={`mb-1 rounded-full p-2.5 text-white shadow-lg transition-all hover:shadow-amber-900/20 ${
            isEditing
              ? 'bg-blue-600 hover:bg-blue-500' // Blue for Edit Save
              : 'bg-amber-600 hover:bg-amber-500' // Amber for Send
          } disabled:cursor-not-allowed disabled:bg-[var(--app-bg-elevated)] disabled:text-[var(--app-text-muted)]`}
          aria-label={isEditing ? t('messages.save') : 'Send message'}
        >
          {isEditing ? (
            <svg className="ml-0.5 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <Send className={`h-5 w-5 ${isSending ? 'animate-pulse' : 'ml-0.5'}`} />
          )}
        </button>
      </div>

      {/* Character count (optional) */}
      {message.length > 1000 && (
        <div className="mt-1 text-right text-xs text-gray-500">{message.length} characters</div>
      )}
    </div>
  )
}
